import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
  MatLegacyDialogModule,
} from '@angular/material/legacy-dialog';
import _ from 'lodash';
import { Subject, merge } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { NgFor, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { MatToolbarModule } from '@angular/material/toolbar';
import { CuttingCustomGrooveSchema } from '../../../models/cutting/cutting-custom-groove-schema.model';
import { GrooveTypeEnum } from '../../../models/cutting/cutting-panel-base-slot.model';
import { CuttingPanelSlot } from '../../../models/cutting/cutting-panel-slot.model';

export interface CustomSlotDetailDialogResponse {
  schemaId: number;
  groove: CuttingPanelSlot;
}

export interface CustomSlotDetailDialogFormData<Schema = CuttingCustomGrooveSchema> {
  schema: Schema;
  designation: string;
  width: number;
  depth: number;
  angle: number;
  radius: number;
  bShift: number;
  shift: number;
}

export class CustomSlotDetailDialogData<Schema = CuttingCustomGrooveSchema, Slot = CuttingPanelSlot> {
  grooveType: GrooveTypeEnum = GrooveTypeEnum.Rabbet;
  customSlotSchemas: Schema[];
  mapSchemaToSlot?: (formData: CustomSlotDetailDialogFormData<Schema>) => Slot;
}

@Component({
  templateUrl: './custom-slot-detail-dialog.component.html',
  standalone: true,
  imports: [
    NgIf,
    MatLegacyFormFieldModule,
    MatLegacyInputModule,
    MatIconModule,
    MatToolbarModule,
    FormsModule,
    ReactiveFormsModule,
    NgFor,
    MatLegacySelectModule,
    MatLegacyDialogModule,
    MatLegacyButtonModule,
    NgSwitch,
    NgSwitchCase,
    MatDividerModule,
  ],
})
export class CustomSlotDetailDialogComponent implements OnInit, OnDestroy {
  grooveForm: UntypedFormGroup;
  thisGrooveTypeEnum = GrooveTypeEnum;
  unsubscribe$: Subject<void> = new Subject();
  grooveType: GrooveTypeEnum;

  get schemas() {
    return this.dialogData.customSlotSchemas;
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    public dialogRef: MatDialogRef<CustomSlotDetailDialogComponent>,
    @Inject(MAT_LEGACY_DIALOG_DATA) private dialogData: CustomSlotDetailDialogData
  ) {}

  ngOnInit(): void {
    this.grooveType = this.dialogData.grooveType;

    this.grooveForm = this.formBuilder.group(this.buildControls(), { validator: this.schemaRangeValidator() });
    this.addControlsListeners();

    if (this.dialogData.customSlotSchemas.length > 0) {
      this.grooveForm.controls.schema.setValue(this.dialogData.customSlotSchemas[0]);
      this.setControls(this.dialogData.customSlotSchemas[0]);
    }
  }

  private buildControls(): any {
    const controls: { [key: string]: any[] } = {
      schema: ['', [Validators.required]],
      designation: [{ value: '' }, [Validators.required]],
    };

    if (
      this.dialogData.grooveType !== GrooveTypeEnum.Chamfer &&
      this.dialogData.grooveType !== GrooveTypeEnum.Chamfer2 &&
      this.dialogData.grooveType !== GrooveTypeEnum.Fillet
    ) {
      controls.width = [0, [Validators.required]];
      controls.depth = [0, [Validators.required]];
    }
    if (this.dialogData.grooveType === GrooveTypeEnum.Chamfer) {
      controls.angle = [0, [Validators.required]];
    }
    if (this.dialogData.grooveType === GrooveTypeEnum.Fillet) {
      controls.radius = [0, [Validators.required]];
    }
    if (this.dialogData.grooveType === GrooveTypeEnum.Chamfer2) {
      controls.bShift = [0, [Validators.required]];
    }
    if (
      this.dialogData.grooveType === GrooveTypeEnum.InPlate ||
      this.dialogData.grooveType === GrooveTypeEnum.InSide ||
      this.dialogData.grooveType === GrooveTypeEnum.Chamfer2
    ) {
      controls.shift = [0, [Validators.required]];
    }

    return controls;
  }

  schemaRangeValidator(): ValidatorFn {
    return (group: UntypedFormGroup): { [key: string]: any } | null => {
      let valid = false;
      const schema = group.controls.schema.value as CuttingCustomGrooveSchema;
      const failControlsKeys: string[] = [];

      for (const key in group.controls) {
        switch (key) {
          case 'width':
            {
              if (group.controls[key].value >= schema.hMin && group.controls[key].value <= schema.hMax) {
                valid = true;
              } else {
                failControlsKeys.push(key);
                group.controls[key].setErrors({ outOfRange: { min: schema.hMin, max: schema.hMax } });
              }
            }
            break;

          case 'depth':
            {
              if (group.controls[key].value >= schema.gMin && group.controls[key].value <= schema.gMax) {
                valid = true;
              } else {
                failControlsKeys.push(key);
                group.controls[key].setErrors({ outOfRange: { min: schema.gMin, max: schema.gMax } });
              }
            }
            break;

          case 'angle':
            {
              if (group.controls[key].value >= schema.fiMin && group.controls[key].value <= schema.fiMax) {
                valid = true;
              } else {
                failControlsKeys.push(key);
                group.controls[key].setErrors({ outOfRange: { min: schema.fiMin, max: schema.fiMax } });
              }
            }
            break;

          case 'shift':
            {
              if (group.controls[key].value >= schema.dxMin && group.controls[key].value <= schema.dxMax) {
                valid = true;
              } else {
                failControlsKeys.push(key);
                group.controls[key].setErrors({ outOfRange: { min: schema.dxMin, max: schema.dxMax } });
              }
            }
            break;

          case 'bShift':
            {
              if (group.controls[key].value >= schema.bMin && group.controls[key].value <= schema.bMax) {
                valid = true;
              } else {
                failControlsKeys.push(key);
                group.controls[key].setErrors({ outOfRange: { min: schema.bMin, max: schema.bMax } });
              }
            }
            break;

          case 'radius':
            {
              if (group.controls[key].value >= schema.rMin && group.controls[key].value <= schema.rMax) {
                valid = true;
              } else {
                failControlsKeys.push(key);
                group.controls[key].setErrors({ outOfRange: { min: schema.rMin, max: schema.rMax } });
              }
            }
            break;
        }
      }

      return valid ? null : { outOfSchemaRange: { control: failControlsKeys } };
    };
  }

  addControlsListeners(): void {
    const updater = (): void => {
      this.updateDesignation(this.grooveForm.controls.schema.value.designation);
    };

    merge(
      ..._.compact([
        this.grooveForm.controls.width?.valueChanges,
        this.grooveForm.controls.depth?.valueChanges,
        this.grooveForm.controls.angle?.valueChanges,
        this.grooveForm.controls.shift?.valueChanges,
        this.grooveForm.controls.bShift?.valueChanges,
        this.grooveForm.controls.radius?.valueChanges,
      ])
    )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(updater);
  }

  setControls(schema: CuttingCustomGrooveSchema): void {
    if (!schema) {
      return;
    }

    if (this.grooveForm.controls.width) {
      this.grooveForm.controls.width.setValue(schema.hMin);
    }
    if (this.grooveForm.controls.depth) {
      this.grooveForm.controls.depth.setValue(schema.gMin);
    }
    if (this.grooveForm.controls.angle) {
      this.grooveForm.controls.angle.setValue(schema.fiMin);
    }
    if (this.grooveForm.controls.shift) {
      this.grooveForm.controls.shift.setValue(schema.dxMin);
    }
    if (this.grooveForm.controls.bShift) {
      this.grooveForm.controls.bShift.setValue(schema.bMin);
    }
    if (this.grooveForm.controls.radius) {
      this.grooveForm.controls.radius.setValue(schema.rMin);
    }
  }

  updateDesignation(designationMask: string): void {
    designationMask ??= '';
    const getRoundedStr = (val: number): string => (Math.round(val * 100) / 100).toString();

    if (this.grooveForm.controls.width) {
      designationMask = designationMask.replace(/%h%/gi, getRoundedStr(this.grooveForm.controls.width.value));
    }
    if (this.grooveForm.controls.depth) {
      designationMask = designationMask.replace(/%g%/gi, getRoundedStr(this.grooveForm.controls.depth.value));
    }
    if (this.grooveForm.controls.angle) {
      designationMask = designationMask.replace(/%fi%/gi, getRoundedStr(this.grooveForm.controls.angle.value));
    }
    if (this.grooveForm.controls.shift) {
      designationMask = designationMask.replace(/%dx%/gi, getRoundedStr(this.grooveForm.controls.shift.value));
    }
    if (this.grooveForm.controls.bShift) {
      designationMask = designationMask.replace(/%b%/gi, getRoundedStr(this.grooveForm.controls.bShift.value));
    }
    if (this.grooveForm.controls.radius) {
      designationMask = designationMask.replace(/%r%/gi, getRoundedStr(this.grooveForm.controls.radius.value));
    }

    this.grooveForm.controls.designation.setValue(designationMask);
  }

  saveAndClose(): void {
    let groove: unknown;

    if (this.dialogData.mapSchemaToSlot) {
      groove = this.dialogData.mapSchemaToSlot(this.grooveForm.value);
    } else {
      const panelGroove = new CuttingPanelSlot();

      panelGroove.name = (this.grooveForm.controls.schema.value as CuttingCustomGrooveSchema).name;
      panelGroove.designation = this.grooveForm.controls.designation.value;
      panelGroove.type = this.dialogData.grooveType;
      panelGroove.h = this.grooveForm.controls.width ? this.grooveForm.controls.width.value : 0;
      panelGroove.g = this.grooveForm.controls.depth ? this.grooveForm.controls.depth.value : 0;
      panelGroove.dx = this.grooveForm.controls.shift ? this.grooveForm.controls.shift.value : 0;
      panelGroove.fi = this.grooveForm.controls.angle ? this.grooveForm.controls.angle.value : 0;
      panelGroove.b = this.grooveForm.controls.bShift ? this.grooveForm.controls.bShift.value : 0;
      panelGroove.r = this.grooveForm.controls.radius ? this.grooveForm.controls.radius.value : 0;

      groove = panelGroove;
    }

    this.dialogRef.close({
      groove: groove,
      schemaId: this.grooveForm.controls.schema.value.id,
    } as CustomSlotDetailDialogResponse);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }
}
